let resolvedApiBaseUrl: string | null = null;

const SESSIONSTORE_OVERRIDE_KEY = '__cp_api_override';
const PROD_APEX_HOST = 'credit-app-api.creditpulse.com';
const VALID_BUILD_REGEX = /^pr-[1-9]\d*$/;

const extractBuildParam = (): string | undefined => {
  const { hash } = window.location;
  const queryStart = hash.indexOf('?');

  if (queryStart !== -1) {
    // slice (not split) so additional '?' chars in a malformed URL stay in the query string
    const queryPortion = hash.slice(queryStart + 1);
    const buildParam = new URLSearchParams(queryPortion).get('build');
    if (buildParam) {
      return buildParam;
    }
  }

  // The app uses hash history, so the param normally lives after the '#'. We also honor it
  // on the real query string so it works whether a user types ?build= before or after the
  // hash. Safe because the whole override mechanism is prod-gated in initializeApiBaseUrl.
  const fallbackBuildParam = new URLSearchParams(window.location.search).get('build');
  return fallbackBuildParam ?? undefined;
};

const isValidBuildParam = (value: string): boolean => {
  return VALID_BUILD_REGEX.test(value) || value === 'dev';
};

const isProdEnvironment = (): boolean => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL ?? '';
    const hostname = new URL(apiUrl).hostname;
    return hostname === PROD_APEX_HOST;
  } catch {
    // Fail closed: an absent or malformed REACT_APP_API_URL is treated as prod so a
    // misconfigured deploy can never enable a build override. Warn so local dev with a
    // missing env var can tell why the override was suppressed.
    console.warn(
      'Could not parse REACT_APP_API_URL; treating environment as production and ignoring any build override.',
    );
    return true;
  }
};

const resolveBuildToUrl = (buildParam: string): string => {
  if (VALID_BUILD_REGEX.test(buildParam)) {
    return `https://${buildParam}.credit-app-api.creditpulse.com/${buildParam}`;
  }

  if (buildParam === 'dev') {
    return 'https://dev.credit-app-api.creditpulse.com/dev';
  }

  // Unreachable: callers must guard with isValidBuildParam first. Fail loudly if a
  // future valid value is added here without a matching branch.
  throw new Error(`Unhandled build parameter: ${buildParam}`);
};

const safeSessionStorageWrite = (key: string, value: string): void => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Silently fail if sessionStorage is unavailable (private browsing, etc.)
  }
};

const safeSessionStorageRead = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    // Silently fail if sessionStorage is unavailable
    return null;
  }
};

const safeSessionStorageRemove = (key: string): void => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
};

export const initializeApiBaseUrl = (): void => {
  resolvedApiBaseUrl = process.env.REACT_APP_API_URL ?? '';

  if (!resolvedApiBaseUrl) {
    // An empty base produces relative request URLs that silently hit window.location.origin.
    console.warn('REACT_APP_API_URL is not set; API requests will use a relative base URL.');
  }

  if (isProdEnvironment()) {
    safeSessionStorageRemove(SESSIONSTORE_OVERRIDE_KEY);
    return;
  }

  const buildParam = extractBuildParam();

  if (buildParam) {
    if (!isValidBuildParam(buildParam)) {
      console.warn(
        `Invalid build parameter: ${buildParam}. Expected format: pr-<number> or 'dev'. Using default API URL.`,
      );
      // An explicit invalid param is a signal to stop overriding — clear any stale stored
      // override so it isn't re-applied on the next init (e.g. a reload that drops the param).
      safeSessionStorageRemove(SESSIONSTORE_OVERRIDE_KEY);
      return;
    }

    resolvedApiBaseUrl = resolveBuildToUrl(buildParam);
    // Persist the build label (not the resolved URL) so read-back is re-validated and
    // re-resolved below — a tampered sessionStorage value can never become the API base.
    safeSessionStorageWrite(SESSIONSTORE_OVERRIDE_KEY, buildParam);
    return;
  }

  const storedBuildParam = safeSessionStorageRead(SESSIONSTORE_OVERRIDE_KEY);
  if (storedBuildParam && isValidBuildParam(storedBuildParam)) {
    resolvedApiBaseUrl = resolveBuildToUrl(storedBuildParam);
  } else if (storedBuildParam) {
    // Stored value is not a valid build label (stale or tampered) — drop it.
    safeSessionStorageRemove(SESSIONSTORE_OVERRIDE_KEY);
  }
};

export const getApiBaseUrl = (): string => {
  if (resolvedApiBaseUrl === null) {
    initializeApiBaseUrl();
  }

  return resolvedApiBaseUrl ?? '';
};

// Test-only: clears the module cache and stored override for test isolation. Do NOT call
// from application code — resetting mid-session would drop the override on the next
// getApiBaseUrl() call if the URL no longer carries the build param.
export const resetApiBaseUrl = (): void => {
  resolvedApiBaseUrl = null;
  safeSessionStorageRemove(SESSIONSTORE_OVERRIDE_KEY);
};
